import { Autocomplete, Box, Button, IconButton, Paper, TextField, Typography } from '@mui/material'
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { useSubscriptions, useKeyVaultsList, useRefreshKeyVaults } from '@src/tools/key-vault/api/keyvaults-api'
import { useCreateSecrets } from '@src/tools/key-vault/api/secrets-api'
import { SecretCreationResult, Subscription } from '@src/tools/key-vault/types/KeyVault'
import { translateError } from '@src/shared/utils/translateError'
import SecretRow from './Components/SecretRow'
import ResultsTable from './Components/ResultsTable'
import { newSecretsSchema, type NewSecretsFormData } from './NewSecretsPage-schema'

export type { NewSecretsFormData }

const NewSecretsPage = () => {
  const { t } = useTranslation()
  const [subscriptions, isLoadingSubscriptions] = useSubscriptions()
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [keyVaults, isLoadingKeyVaults] = useKeyVaultsList(selectedSubscription?.id ?? null)
  const [refreshKeyVaults, isRefreshing] = useRefreshKeyVaults(selectedSubscription?.id ?? null)
  const [createSecrets, isPending] = useCreateSecrets()

  const [mode, setMode] = useState<'form' | 'results'>('form')
  const [results, setResults] = useState<SecretCreationResult[]>([])
  const [pendingKeys, setPendingKeys] = useState<string[]>([])

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<NewSecretsFormData>({
    defaultValues: {
      keyVaultName: '',
      secrets: [{ key: '', value: '' }],
    },
    resolver: yupResolver(newSecretsSchema),
    mode: 'onBlur',
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'secrets' })
  const keyVaultName = watch('keyVaultName')

  const handleSubscriptionChange = (_e: unknown, value: Subscription | null) => {
    setSelectedSubscription(value)
    setValue('keyVaultName', '')
  }

  const onSubmit = async (data: NewSecretsFormData) => {
    setPendingKeys(data.secrets.map((s) => s.key))
    setResults([])
    setMode('results')

    try {
      const response = await createSecrets({
        keyVaultName: data.keyVaultName,
        secrets: data.secrets,
      })
      setResults(response)
    } catch {
      setResults(
        data.secrets.map((s) => ({
          secretName: s.key,
          status: 'Error' as const,
          message: t('keyVault.unexpectedError'),
        })),
      )
    }
  }

  const handleBackToForm = () => {
    const failedSecrets = results
      .filter((r) => r.status !== 'Success')
      .map((r) => {
        const original = watch('secrets').find((s) => s.key === r.secretName)
        return { key: r.secretName, value: original?.value ?? '' }
      })

    reset({
      keyVaultName,
      secrets: failedSecrets.length > 0 ? failedSecrets : [{ key: '', value: '' }],
    })

    setMode('form')
    setResults([])
    setPendingKeys([])
  }

  const allResultsReceived = results.length === pendingKeys.length

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('keyVault.newSecrets')}
      </Typography>

      {mode === 'form' ? (
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
              <Autocomplete
                options={subscriptions}
                getOptionLabel={(option) => option.name}
                loading={isLoadingSubscriptions}
                value={selectedSubscription}
                onChange={handleSubscriptionChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('keyVault.subscription')} size="small" required />
                )}
                sx={{ maxWidth: 400, flex: 1 }}
              />
              <Autocomplete
                options={keyVaults}
                loading={isLoadingKeyVaults || isRefreshing}
                value={keyVaultName || null}
                onChange={(_e, value) => setValue('keyVaultName', value ?? '')}
                disabled={!selectedSubscription}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('keyVault.keyVaultName')}
                    size="small"
                    required
                    error={!!errors.keyVaultName}
                    helperText={translateError(t, errors.keyVaultName)}
                  />
                )}
                sx={{ maxWidth: 400, flex: 1 }}
              />
              <IconButton
                onClick={() => refreshKeyVaults()}
                disabled={isRefreshing || !selectedSubscription}
                title={t('keyVault.refreshKeyVaults')}
                sx={{ mt: 0.25 }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>

            {fields.map((field, index) => (
              <SecretRow
                key={field.id}
                index={index}
                control={control}
                errors={errors}
                onRemove={() => remove(index)}
                disabled={false}
                canRemove={fields.length > 1}
              />
            ))}

            <Button startIcon={<AddIcon />} onClick={() => append({ key: '', value: '' })} sx={{ mt: 1, mb: 3 }}>
              {t('keyVault.addSecret')}
            </Button>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider', pt: 2 }}>
              <Button
                type="button"
                onClick={() =>
                  reset({
                    keyVaultName: '',
                    secrets: [{ key: '', value: '' }],
                  })
                }
              >
                {t('keyVault.clearAll')}
              </Button>
              <Button type="submit" variant="contained">
                {t('keyVault.save')}
              </Button>
            </Box>
          </form>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <ResultsTable results={results} pendingKeys={pendingKeys} />
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleBackToForm} disabled={!allResultsReceived || isPending}>
              {t('keyVault.backToNewSecrets')}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default NewSecretsPage
